import { useToast } from '@contexts/Toast';
import {
  errorMessage,
  formRegex,
  ProductType,
  WeightType,
} from '@helpers/Constants';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import {
  Addon,
  AddonsList,
  ProductDetails,
  SelectedProduct,
} from '@helpers/Models';
import { Axios } from '@helpers/ApiService';
import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faSquareCheck,
  faSquareXmark,
} from '@fortawesome/free-solid-svg-icons';

export const AddonSelectionSheet: React.FC<{
  productDetails: ProductDetails;
  onClose: () => void;
  onAdd: (payload: SelectedProduct) => void;
}> = ({ productDetails, onClose, onAdd }) => {
  const [addons, setAddons] = useState<AddonsList[]>([]);
  const { showToastMessage } = useToast();
  const [searchKey, setSearchKey] = useState('');
  const [isFatInputVisible, setIsFatInputVisible] = useState(false);
  const [fatPercentage, setFatPercentage] = useState<number | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<AddonsList[]>([]);
  const [minQuantityError, setMinQuantityError] = useState<string | null>('');
  const [quantity, setQuantity] = useState<number | string>(1);
  // Set weight to 1 lb by default if it is 0 lb.
  // Declare a new constant to avoid interfering with productDetails,
  // which is used for display purposes.
  const updatedWeight = productDetails.weight === 0 ? 1 : productDetails.weight;

  useEffect(() => {
    fetchAddons(searchKey);
    // Using same quantity key for both the product types
    if (productDetails.productType == ProductType.QUANTITY) {
      setQuantity(productDetails.minQuantity);
    } else {
      // Set weight to 1 lb by default if it is 0.
      setQuantity(productDetails.weight === 0 ? 1 : productDetails.weight);
    }
  }, [searchKey]);

  /**
   * Fetches add-on items from the server based on a search key.
   *
   * Sends a POST request to the `FETCH_ADDONS` API endpoint with the provided `searchKey`.
   * On success:
   * - Updates the `addons` state with the fetched add-on list.
   * On failure:
   * - Displays an error toast message.
   * - Rethrows the error for potential upstream error handling.
   *
   * @param {string} searchKey - The string used to filter or search add-ons on the server.
   * @returns {Promise<void>} A promise that resolves when the fetch and state update are complete.
   *
   * Dependencies:
   * - Axios for HTTP requests.
   * - ApiEndpoints.FETCH_ADDONS
   * - showToastMessage for user feedback.
   */
  const fetchAddons = async (searchKey: string): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_ADDONS, {
        searchKey,
      });
      console.log(response.data.data.addOns);
      //filter through the addons and set only the addons where availableFlag is 1
      const availableAddons = response.data.data.addOns.filter(
        (addon: Addon) => addon.availableFlag === 1,
      );
      setAddons(availableAddons);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Toggles the selection state of an addon in the selected addons list.
   *
   * If the addon is already selected (exists in the `selectedAddons` state),
   * it will be removed from the list. Otherwise, it will be added.
   *
   * @param {AddonsList} addon - The addon item to toggle in the selection.
   *
   * @returns {void}
   */
  const toggleAddonSelection = (addon: AddonsList): void => {
    setSelectedAddons((prev) => {
      const exists = prev.find((a) => a.id === addon.id);
      if (exists) {
        return prev.filter((a) => a.id !== addon.id);
      } else {
        return [...prev, addon];
      }
    });
  };

  /**
   * Handles the addition of a product to the cart/order with validation rules
   * based on the product type.
   *
   * Validation:
   * - If the product type is `1` (weight-based), the entered `quantity` must be
   *   at least the product's required weight. Otherwise, a weight requirement
   *   error is shown.
   * - If the product type is `2` (quantity-based), the entered `quantity` must be
   *   at least the product's `minQuantity`. Otherwise, a minimum quantity error
   *   is shown.
   *
   * Behavior:
   * - If validation passes, clears any minimum quantity error.
   * - Prepares a `payload` object with product details, quantity, fat percentage,
   *   and selected add-on IDs.
   * - Calls the `onAdd` callback with the payload.
   * - Closes the dialog by calling `onClose`.
   *
   * @returns {void}
   */
  const handleAdd = (): void => {
    // Validation check
    switch (productDetails.productType) {
      case 1:
        // Use 'updatedWeight' as it has the updated 1 lb value.
        if (Number(quantity) < updatedWeight) {
          setMinQuantityError(
            `${errorMessage.PRODUCT_WEIGHT_REQUIREMENT} ${updatedWeight} ${WeightType.LBS}`,
          );
          return;
        }
        break;
      case 2:
        if (Number(quantity) < productDetails.minQuantity) {
          setMinQuantityError(
            `${errorMessage.PRODUCT_MINQUANTITY_REQUIREMENT} ${productDetails.minQuantity}`,
          );
          return;
        }
        break;
    }
    setMinQuantityError(null);
    const payload = {
      productId: productDetails.id,
      quantity: Number(quantity),
      fatPercent: fatPercentage ?? 0,
      addOnIds: selectedAddons
        .map((addon) => addon.id)
        .filter((id): id is number => id !== undefined),
    };
    onAdd(payload);
    onClose();
  };

  /**
   * Conditionally renders the fat percentage input section:
   *
   * - Only renders if productDetails.allowFatContentFlag is 1
   * - If fat input is hidden and no fat percentage is set:
   *   - Shows a button labeled "Add Fat %" that toggles visibility.
   *
   * - If fat input is currently visible:
   *   - Shows an input field (0–100 range) to set fat percentage.
   *
   * - If fat percentage is set and input is hidden:
   *   - Shows a button displaying the current fat percentage value.
   */
  let fatInputSection;
  if (productDetails.allowFatContentFlag === 1) {
    if (!isFatInputVisible && fatPercentage === null) {
      fatInputSection = (
        <button
          type="button"
          className="w-full mt-6 px-4 py-1.5 border rounded-sm text-sm font-medium text-black bg-white border-[#00000026]"
          onClick={() => setIsFatInputVisible(true)}
        >
          Add Fat %
        </button>
      );
    } else if (isFatInputVisible) {
      fatInputSection = (
        <div className="w-full mt-6 flex items-center gap-2">
          <input
            type="number"
            min="0"
            max="100"
            value={fatPercentage ?? ''}
            onChange={(e) => {
              const val = e.target.value === '' ? null : Number(e.target.value);
              if (val === null || (val >= 0 && val <= 100)) {
                setFatPercentage(val);
              }
            }}
            placeholder="Enter fat %"
            className="flex-grow px-4 py-1.5 border rounded-sm text-sm focus:outline-primary"
          />
          <FontAwesomeIcon
            onClick={() => setIsFatInputVisible(false)}
            icon={faSquareCheck}
            className="mx-1 text-primary text-xl cursor-pointer"
          />
          <FontAwesomeIcon
            onClick={() => {
              setFatPercentage(null);
              setIsFatInputVisible(false);
            }}
            icon={faSquareXmark}
            className="text-gray-500 text-xl cursor-pointer"
          />
        </div>
      );
    } else {
      fatInputSection = (
        <button
          type="button"
          className="w-full mt-6 px-6 py-1 border rounded-sm text-sm text-black bg-white flex justify-center items-center cursor-pointer"
          onClick={() => setIsFatInputVisible(true)}
        >
          <span className="text-center">{fatPercentage}%</span>
        </button>
      );
    }
  } else {
    // If allowFatContentFlag is 0, don't show any fat input section
    fatInputSection = null;
  }

  /**
   * Handles user input for a measurement field.
   *
   * This function updates the `quantity` state based on the provided string value.
   * - If the input is an empty string, it allows clearing the field by storing an empty string.
   * - Otherwise, it attempts to convert the input to a number and updates the state
   *   only if the conversion results in a valid numeric value.
   *
   * @param {string} value - The input string from the measurement field.
   * @returns {void}
   */
  const handleMeasurementChange = (value: string): void => {
    // Allow users to add empty strings for clearing the input
    if (value === '') {
      // store empty string when cleared
      setQuantity('');
    } else {
      // Convert input string to number and
      // update quantity if it's a valid number
      if (!isNaN(Number(value))) {
        setQuantity(Number(value));
      } else {
        // Invalid entry, keep the current quantity
        return;
      }
    }
  };

  /**
   * Handles blur events for decimal number inputs.
   *
   * This function ensures that when the user leaves an input field:
   * - If the field is empty, it resets the quantity to an empty string.
   * - If the input is a valid decimal (up to two decimal places), it stores the numeric value.
   * - If the input exceeds two decimal places (e.g., "12.345"), it rounds the value to two decimals
   *   and updates both the state and the visible input value.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event
   *   The change event triggered when the input loses focus.
   *
   * @returns {void}
   *
   */
  const handleOnBlur = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    // If input is cleared, set the value to null
    if (value === '') {
      setQuantity('');
      return;
    }
    // validate the decimal value
    if (formRegex.DECIMAL_UP_TO_TWO_PLACES.test(value)) {
      setQuantity(Number(value));
      return;
    } else {
      // Will get executed when user enters 0s after the 2 decimal points
      // Round to 2 decimals
      setQuantity(Number(value).toFixed(2));
      // To reflect in the input field
      event.target.value = Number(value).toFixed(2);
    }
  };

  return (
    <div className="fixed z-10 inset-0 bg-black bg-opacity-40 flex justify-center items-end w-full h-full">
      <div className="bg-white rounded-t-2xl w-full p-6 shadow-lg transition-transform transform translate-y-0 max-h-[70vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h1 className="font-semibold text-lg">{productDetails.name}</h1>
          <div className="flex items-end gap-2">
            <button
              onClick={() => onClose()}
              type="button"
              className="text-gray-600 hover:text-gray-900"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="m-4">
          <div className="items-center">
            <div className="flex items-start justify-between gap-5">
              <div className="w-1/2">
                <div className="border p-4 rounded-lg shadow-md bg-[#FFFEFA]">
                  <div className="flex justify-between font-bold text-lg text-gray-700">
                    <div>{productDetails.name}</div>
                    <div>
                      {/* Display Item as mintype for product type quantity and lbs for weight */}
                      ${productDetails.price}/
                      {productDetails.productType === ProductType.QUANTITY
                        ? 'Item'
                        : `${WeightType.LBS}`}
                    </div>
                  </div>
                  <div className="mt-12">
                    <span className="text-sm text-gray-600 mb-3 font-semibold">
                      {productDetails.productType === ProductType.QUANTITY
                        ? `Min Quantity: ${productDetails.minQuantity}`
                        : `Min Weight: ${productDetails.weight} ${WeightType.LBS}`}
                    </span>
                  </div>
                </div>
              </div>
              <div className="w-1/2">
                <div className="p-4 w-full">
                  {productDetails.productType == ProductType.QUANTITY ? (
                    // If product type is 2 - quantity
                    <div className="flex items-center w-full gap-3">
                      <span className="font-semibold text-[#1C1B1F] whitespace-nowrap">
                        Quantity:
                      </span>
                      <div className="flex items-center flex-grow">
                        <button
                          // Using math to ensure that the quantity doesn't go below 1.
                          onClick={() =>
                            setQuantity((prev) => {
                              // Fallback, if empty, reset to 1
                              if (prev === '') return 1;
                              return Math.max(1, Number(prev) - 1);
                            })
                          }
                          type="button"
                          className="w-16 h-8 flex items-center justify-center border rounded-l text-white text-lg bg-primary hover:bg-primary"
                        >
                          -
                        </button>
                        <input
                          type="text"
                          value={quantity ?? ''}
                          onChange={(event) =>
                            handleMeasurementChange(event.target.value)
                          }
                          className="w-full h-8 text-center border-t border-b focus:outline-primary"
                        />
                        <button
                          onClick={() =>
                            setQuantity((prev) =>
                              prev === '' ? 1 : Number(prev) + 1,
                            )
                          }
                          type="button"
                          className="w-16 h-8 flex items-center justify-center border rounded-r text-white text-lg bg-primary hover:bg-primary"
                        >
                          +
                        </button>
                        <span className="ml-2 text-gray-400 w-8">
                          {productDetails.minType}
                        </span>
                      </div>
                    </div>
                  ) : (
                    // If product type is 1 - weight
                    <div className="flex items-center w-full gap-3">
                      <span className="font-semibold text-[#1C1B1F] whitespace-nowrap">
                        Weight:
                      </span>
                      <input
                        type="text"
                        name="weight"
                        value={quantity}
                        onBlur={handleOnBlur}
                        onChange={(event) => {
                          setQuantity(event.target.value);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-gray-320 focus:outline-primary outline-gray-300"
                      />
                      <span>{WeightType.LBS}</span>
                    </div>
                  )}

                  {minQuantityError && (
                    <span className="text-red-500">{minQuantityError}</span>
                  )}
                  {fatInputSection}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-400 w-full mb-6" />
        <div className="flex justify-between items-center">
          <h1 className="font-semibold text-lg">
            Choose Add-Ons
            {productDetails.upchargeIngredientsFlag === 0 ? (
              <span className="ml-2">(No Up-Charge)</span>
            ) : null}
          </h1>
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="text"
                value={searchKey}
                placeholder="Search..."
                onChange={(e) => setSearchKey(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 h-8 w-80 text-sm"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
              />
            </div>
          </div>
        </div>
        <div className="overflow-y-auto max-h-[60vh] mt-3">
          <ul className="space-y-2">
            {addons.map((addon) => {
              const isSelected = selectedAddons.some((a) => a.id === addon.id);
              return (
                <li
                  key={addon.id}
                  className={`p-3 rounded-lg cursor-pointer transition ${
                    isSelected
                      ? 'bg-tertiary border border-primary'
                      : 'bg-white border border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="font-semibold">{addon.name}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-[#43474E]">
                        {productDetails.upchargeIngredientsFlag === 0
                          ? '$ 0'
                          : `+$${addon.price?.toFixed(2)}`}
                        /{WeightType.LBS}
                      </span>
                      <input
                        checked={isSelected}
                        onChange={() => toggleAddonSelection(addon)}
                        type="checkbox"
                        className="w-5 h-5 accent-primary"
                      />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="sticky bottom-0 bg-white mt-3 p-1">
          <div className="flex justify-between gap-4 mt-2">
            <button
              onClick={() => onClose()}
              type="button"
              className="w-[50%] px-6 py-1 border border-primary rounded-sm text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              type="button"
              className="w-[50%] px-6 py-1 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary"
            >
              Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
